import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AddOutlined, DeleteOutline } from "@mui/icons-material";
import useStyles from "./styles";
import {
	SEVERIDADES,
	LABEL_SEVERIDADE,
} from "../../../../utils/centralDeAlertas/labels";
import {
	SINAL_ADITIVO,
	SINAL_SUBTRATIVO,
	LABEL_SINAL,
	TAMANHO_MAX_PADRAO,
	avaliarRegras,
	atendeSeveridadeMinima,
	padraoInvalido,
} from "../../../../utils/centralDeAlertas/regrasTipoAlerta";

const SEVERIDADE_TESTE_PADRAO = "medio";

// Editor das regras de casamento de UM tipo de alerta: lista de regex com
// sinal (incluir/excluir) + piso de severidade para exibição, com um testador
// que roda a mesma lógica do backend contra um título colado pelo operador.
export default function ModalTipoAlerta({ open, tipo, onClose, onSalvar }) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [regras, setRegras] = useState([]);
	const [severidadeMinima, setSeveridadeMinima] = useState("");
	const [tituloTeste, setTituloTeste] = useState("");
	const [severidadeTeste, setSeveridadeTeste] = useState(
		SEVERIDADE_TESTE_PADRAO
	);
	const [salvando, setSalvando] = useState(false);

	// Recarrega do tipo a cada abertura (edição local; só vai ao backend ao
	// salvar, senão cada tecla viraria um POST).
	useEffect(() => {
		if (!open) return;
		setRegras(
			Array.isArray(tipo?.regras)
				? tipo.regras.map((r) => ({
					sinal: r?.sinal === SINAL_SUBTRATIVO ? SINAL_SUBTRATIVO : SINAL_ADITIVO,
					padrao: String(r?.padrao ?? ""),
				}))
				: []
		);
		setSeveridadeMinima(tipo?.severidadeMinima || "");
		setTituloTeste("");
		setSeveridadeTeste(SEVERIDADE_TESTE_PADRAO);
		setSalvando(false);
	}, [open, tipo]);

	const atualizarRegra = (indice, campo, valor) =>
		setRegras((atual) =>
			atual.map((r, i) => (i === indice ? { ...r, [campo]: valor } : r))
		);

	const removerRegra = (indice) =>
		setRegras((atual) => atual.filter((_, i) => i !== indice));

	const adicionarRegra = () =>
		setRegras((atual) => [...atual, { sinal: SINAL_ADITIVO, padrao: "" }]);

	const preenchidas = useMemo(
		() => regras.filter((r) => r.padrao.trim()),
		[regras]
	);
	const temInvalida = regras.some(
		(r) => padraoInvalido(r.padrao) || r.padrao.length > TAMANHO_MAX_PADRAO
	);
	// Mesma recusa do backend: só subtrativas nunca casariam alerta nenhum.
	const semAditiva =
		preenchidas.length > 0 &&
		!preenchidas.some((r) => r.sinal === SINAL_ADITIVO);

	const resultado = useMemo(() => {
		if (!tituloTeste.trim()) return null;
		const avaliacao = avaliarRegras(tituloTeste, preenchidas);
		const noPiso = atendeSeveridadeMinima(severidadeTeste, severidadeMinima);
		return { ...avaliacao, noPiso };
	}, [tituloTeste, preenchidas, severidadeTeste, severidadeMinima]);

	const handleSalvar = async () => {
		setSalvando(true);
		try {
			await onSalvar({
				id: tipo?.id,
				regras: preenchidas.map((r) => ({
					sinal: r.sinal,
					padrao: r.padrao.trim(),
				})),
				severidadeMinima: severidadeMinima || null,
			});
		} finally {
			setSalvando(false);
		}
	};

	const corVeredito = () => {
		if (!resultado) return theme.palette.text.secondary;
		if (!resultado.casa) return theme.palette.text.secondary;
		return resultado.noPiso
			? theme.palette.success.main
			: theme.palette.warning.main;
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>Regras de "{tipo?.nome}"</DialogTitle>
			<DialogContent>
				<Box sx={styles.explicacao}>
					<Typography variant="body2">
						Um alerta é deste tipo quando o título casa <b>alguma</b> regra de
						incluir e <b>nenhuma</b> de excluir — excluir é veto, não
						alternativa.
					</Typography>
					<Typography variant="body2" sx={{ mt: 0.5 }}>
						A severidade mínima não muda esse casamento: ela decide a partir de
						qual severidade o alerta <b>aparece no painel</b>. Abaixo do piso
						ele entra na contagem de "alertas ocultos pela configuração".
					</Typography>
				</Box>

				<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
					Regras de casamento do título
				</Typography>

				{regras.map((regra, indice) => (
					<Box key={indice} sx={styles.linhaRegra}>
						<TextField
							select
							size="small"
							label="Sinal"
							value={regra.sinal}
							onChange={(e) => atualizarRegra(indice, "sinal", e.target.value)}
							sx={styles.campoSinal}
						>
							<MenuItem value={SINAL_ADITIVO}>
								{LABEL_SINAL[SINAL_ADITIVO]}
							</MenuItem>
							<MenuItem value={SINAL_SUBTRATIVO}>
								{LABEL_SINAL[SINAL_SUBTRATIVO]}
							</MenuItem>
						</TextField>
						<TextField
							fullWidth
							size="small"
							label="Expressão regular"
							value={regra.padrao}
							onChange={(e) => atualizarRegra(indice, "padrao", e.target.value)}
							error={
								padraoInvalido(regra.padrao) ||
								regra.padrao.length > TAMANHO_MAX_PADRAO
							}
							helperText={
								padraoInvalido(regra.padrao)
									? "Expressão regular inválida"
									: regra.padrao.length > TAMANHO_MAX_PADRAO
										? `Máximo de ${TAMANHO_MAX_PADRAO} caracteres`
										: " "
							}
						/>
						<Tooltip title="Remover regra">
							<IconButton size="small" onClick={() => removerRegra(indice)}>
								<DeleteOutline fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				))}

				{!regras.length && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Sem regras: este tipo não casa alerta nenhum.
					</Typography>
				)}

				<Button size="small" startIcon={<AddOutlined />} onClick={adicionarRegra}>
					Adicionar regra
				</Button>

				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mt: 1 }}
				>
					Texto simples já funciona ("temperatura"). Para casar a palavra
					inteira use \b — \bdown\b pega "Link down" sem pegar "download".
				</Typography>

				{semAditiva && (
					<Typography variant="caption" color="error" sx={{ display: "block" }}>
						É preciso ao menos uma regra de incluir — só exclusões nunca casam
						alerta nenhum.
					</Typography>
				)}

				<Divider sx={{ my: 2 }} />

				<TextField
					select
					size="small"
					label="Severidade mínima para exibir"
					value={severidadeMinima}
					onChange={(e) => setSeveridadeMinima(e.target.value)}
					sx={{ minWidth: 260 }}
					helperText={
						severidadeMinima
							? `Alertas abaixo de ${LABEL_SEVERIDADE[severidadeMinima]} ficam ocultos no painel`
							: "Qualquer severidade aparece"
					}
				>
					<MenuItem value="">Qualquer severidade</MenuItem>
					{SEVERIDADES.map((s) => (
						<MenuItem key={s} value={s}>
							{LABEL_SEVERIDADE[s]} ou pior
						</MenuItem>
					))}
				</TextField>

				<Box sx={styles.blocoTeste}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
						Testar com um título real
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						<TextField
							size="small"
							sx={{ flex: 1, minWidth: 260 }}
							label="Título do alerta"
							placeholder="Ex.: Interface eth0: Link down"
							value={tituloTeste}
							onChange={(e) => setTituloTeste(e.target.value)}
						/>
						<TextField
							select
							size="small"
							label="Severidade"
							value={severidadeTeste}
							onChange={(e) => setSeveridadeTeste(e.target.value)}
							sx={{ width: 160 }}
						>
							{SEVERIDADES.map((s) => (
								<MenuItem key={s} value={s}>
									{LABEL_SEVERIDADE[s]}
								</MenuItem>
							))}
						</TextField>
					</Box>

					{resultado && (
						<Box sx={styles.veredito(corVeredito())}>
							<Typography
								variant="body2"
								sx={styles.tituloVeredito(corVeredito())}
							>
								{!resultado.casa
									? "Não é deste tipo"
									: resultado.noPiso
										? "É deste tipo e aparece no painel"
										: "É deste tipo, mas fica oculto pelo piso de severidade"}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{resultado.subtrativaVetou
									? `Vetado pela exclusão "${resultado.subtrativaVetou}"`
									: resultado.aditivasCasadas.length
										? `Casou: ${resultado.aditivasCasadas.join(", ")}`
										: "Nenhuma regra de incluir casou"}
							</Typography>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={salvando}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleSalvar}
					disabled={salvando || temInvalida || semAditiva}
				>
					Salvar
				</Button>
			</DialogActions>
		</Dialog>
	);
}
